import _ from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { useI18n } from "Hooks";
import withDerivedProps from "lib/withDerivedProps";

import { GradebookDataGrid } from "@toddle-edu/ds-data-grid";
import { Button } from "@toddle-edu/ds-web";
import { AddOutlined } from "@toddle-edu/ds-icons";
import { colors } from "Constants";

import {
  getDatagridHeaders,
  recalculateGradeScaleValues,
  getNewRow,
  getGridData,
  colorPalette,
} from "./utilsLegacy";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { updateDisplaySequenceForGradeScaleValues } from "../../routes/GradeScales/modules/GradeScalesUtils";
import {
  shouldShowLevelsInGradingSetupV2,
  getShouldHideDefaultLevelInGradeScale,
  getIsAbbreviationLengthLimited,
} from "../../modules/Utils";
import {
  ALPHA,
  CUSTOM,
  DEPENDENT,
  MIDDLE,
  NUMERIC,
  REGULAR,
  SPECIAL,
} from "Constants/stringConstants";

import classes from "./GradeScaleDatagrid.scss";

const styles = {
  emptyCellStyle: {
    bgColor: "transparent",
    borderColor: colors.borderSelected,
  },
};

const GradeScaleDatagridLegacy = props => {
  const {
    gradeScaleType,
    gradeScaleValuesData,
    setGradeScaleValuesData,
    gradeValueMethod,
    setGradeValueMethod,
    gradeType,
    isMasteryLevelEnabled,
    isUseForCreditGPACalculation,
    selectedDependentGradeScales: _selectedDependentGradeScales,
    isEditMode,
    showActions,
    differentGradebookValuesForLevels,
    levels,
    curriculumType,
    allGradeScalesData,
    specialGradesTemplateId,
    specialGradeScale,
    isUpdatingExistingTemplate,
    columnWidthMap = {},
    isFromCourseConfig,
    minValue,
    maxValue,
    calculateStandardGradesUsingNumeric,
    showErrors,
    validationFields,
    containerStyle = {},
    isGradeScaleBeingUsed,
  } = props;

  const { t } = useI18n();

  const isAbbreviationLengthLimited = getIsAbbreviationLengthLimited();

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      const width = el.clientWidth;
      const styles = getComputedStyle(el);
      const paddingLeft = styles.paddingLeft;
      const paddingRight = styles.paddingRight;
      const widthWithoutPadding =
        width - parseFloat(paddingLeft) - parseFloat(paddingRight); // subtracting a bit extra to account for table borders
      setContainerWidth(widthWithoutPadding);
    }
  }, [containerRef]);

  const shouldHideDefaultLevel = getShouldHideDefaultLevelInGradeScale({
    levels,
    curriculumType,
    differentGradebookValuesForLevels,
  });

  // handle levels changes
  useEffect(() => {
    if (!_.isEmpty(levels) && gradeScaleType !== DEPENDENT) {
      // Handle gradeScaleValuesData conversion
      const updatedRows = gradeScaleValuesData.map(row => {
        const scores = _.get(row, "gradeScaleValueScores", []);
        if (differentGradebookValuesForLevels) {
          // Going from false -> true: Copy single score to all levels
          const hideDefaultLevel = getShouldHideDefaultLevelInGradeScale({
            levels,
            curriculumType,
            differentGradebookValuesForLevels,
          });
          if (scores.length > 1) {
            // Remove the default level row data incase of DP
            if (hideDefaultLevel) {
              return {
                ...row,
                gradeScaleValueScores: _.filter(
                  scores,
                  score => !_.isNil(_.get(score, "subjectLevel.id", null))
                ),
              };
            }
            return row;
          }

          const baseScore = scores[0] || {};
          const defaultLevelScores = hideDefaultLevel
            ? []
            : [
                {
                  subjectLevel: {
                    id: null,
                  },
                  minValue: baseScore.minValue ?? 0,
                  maxValue: baseScore.maxValue ?? 100,
                  gradeValue: baseScore.gradeValue ?? 0,
                  isMasteryLevel: baseScore.isMasteryLevel ?? false,
                },
              ];
          const perLevelScores = levels.map(level => ({
            subjectLevel: {
              id: level.id,
            },
            minValue: baseScore.minValue ?? 0,
            maxValue: baseScore.maxValue ?? 100,
            gradeValue: baseScore.gradeValue ?? 0,
            isMasteryLevel: baseScore.isMasteryLevel ?? false,
          }));

          return {
            ...row,
            gradeScaleValueScores: [...defaultLevelScores, ...perLevelScores],
          };
        } else {
          // Going from true -> false: Take first level's data as the default score
          if (
            scores.length === 1 &&
            !_.get(scores[0], "subjectLevel.id", null)
          ) {
            return row;
          }

          const firstScore = scores[0] || {};

          return {
            ...row,
            gradeScaleValueScores: [
              {
                minValue: firstScore.minValue ?? 0,
                maxValue: firstScore.maxValue ?? 100,
                gradeValue: firstScore.gradeValue ?? 0,
                isMasteryLevel: firstScore.isMasteryLevel ?? false,
              },
            ],
          };
        }
      });
      setGradeScaleValuesData(
        updateDisplaySequenceForGradeScaleValues(updatedRows)
      );

      // Handle gradeValueMethod conversion
      if (differentGradebookValuesForLevels) {
        // Going from false -> true: copy current method to all levels
        const methodToCopy = gradeValueMethod;
        let newMethodArr = methodToCopy;
        const hideDefaultLevel = getShouldHideDefaultLevelInGradeScale({
          levels,
          curriculumType,
          differentGradebookValuesForLevels,
        });
        if (!_.isArray(methodToCopy)) {
          const defaultLevelMethods = hideDefaultLevel
            ? []
            : [
                {
                  subjectLevel: {
                    id: null,
                  },
                  gradeValueCalculateType: methodToCopy || MIDDLE,
                },
              ];
          const perLevelMethods = levels.map(level => ({
            subjectLevel: {
              id: level.id,
            },
            gradeValueCalculateType: methodToCopy || MIDDLE,
          }));
          newMethodArr = [...defaultLevelMethods, ...perLevelMethods];
        }

        setGradeValueMethod(newMethodArr);
      } else {
        // Going from true -> false: use first element's method
        if (_.isArray(gradeValueMethod)) {
          const methodValue =
            _.get(gradeValueMethod, "[0].gradeValueCalculateType") || MIDDLE;
          setGradeValueMethod(methodValue);
        }
      }
    }
  }, [differentGradebookValuesForLevels]);

  // handle minValue, maxValue changes
  useEffect(() => {
    if (
      !_.includes([REGULAR, DEPENDENT], gradeScaleType) ||
      gradeType !== NUMERIC ||
      !maxValue ||
      minValue < 0 ||
      maxValue <= minValue
    ) {
      return;
    }

    // Separate existing data into regular and special grades
    const existingSpecialGrades = _.filter(
      gradeScaleValuesData,
      item => _.get(item, "type") === "SPECIAL"
    );
    const existingRegularGrades = _.filter(
      gradeScaleValuesData,
      item => _.get(item, "type", "REGULAR") !== "SPECIAL"
    );

    const existingRowsByAbbr = _.keyBy(
      existingRegularGrades,
      row => row.abbreviation
    );

    const updatedRows = [];
    for (let abbr = maxValue; abbr >= minValue; abbr--) {
      const abbrStr = abbr.toString();
      if (existingRowsByAbbr[abbrStr]) {
        updatedRows.push({
          ...existingRowsByAbbr[abbrStr],
          abbreviation: abbrStr,
        });
      } else {
        const newRow = getNewRow({
          gradeScaleType,
          differentGradebookValuesForLevels,
          levels,
          shouldHideDefaultLevel,
        });
        updatedRows.push({ ...newRow, abbreviation: abbrStr });
      }
    }

    // Combine regular rows with special grades
    const combinedRows = [...updatedRows, ...existingSpecialGrades];

    setGradeScaleValuesData(
      updateDisplaySequenceForGradeScaleValues(combinedRows)
    );
  }, [minValue, maxValue, gradeScaleType, gradeType]);

  const showMasteryLevel = gradeScaleType === REGULAR && isMasteryLevelEnabled;
  const showCreditGpaCalculation =
    !_.includes([SPECIAL, DEPENDENT], gradeScaleType) &&
    isUseForCreditGPACalculation;

  // get selectedDependentGradeScales either from props or from gradeScaleValuesData
  const selectedDependentGradeScales =
    _.isEmpty(_selectedDependentGradeScales) && !_.isEmpty(gradeScaleValuesData)
      ? _.uniq(
          _.map(
            _.get(gradeScaleValuesData, "[0].dependentGradeScaleValues"),
            item => {
              return _.get(item, "gradeScale.id");
            }
          )
        )
      : _selectedDependentGradeScales;

  const selectedDependentGradeScalesDetails = _.isEmpty(allGradeScalesData)
    ? _.map(
        _.get(gradeScaleValuesData, "[0].dependentGradeScaleValues"),
        item => {
          return {
            id: _.get(item, "gradeScale.id"),
            templateName: _.get(item, "gradeScale.templateName"),
          };
        }
      )
    : _.map(
        _.filter(allGradeScalesData, item => {
          return _.includes(
            selectedDependentGradeScales,
            _.get(item, "node.id")
          );
        }),
        item => {
          return {
            id: _.get(item, "node.id"),
            templateName: _.get(item, "node.templateName"),
          };
        }
      );

  const handleGradeValueChange = (value, subjectLevelId) => {
    const newMethod = _.get(value, "key");
    let newGradeValueMethod = gradeValueMethod;
    if (differentGradebookValuesForLevels) {
      if (_.isArray(gradeValueMethod)) {
        // change default level method
        const defaultLevelMethod = subjectLevelId
          ? _.get(
              _.find(
                gradeValueMethod,
                item => _.get(item, "subjectLevel.id", null) === null
              ),
              "gradeValueCalculateType"
            )
          : newMethod;

        newGradeValueMethod = [
          {
            subjectLevel: {
              id: null,
            },
            gradeValueCalculateType: defaultLevelMethod,
          },
          ..._.map(levels, level => {
            if (level.id === subjectLevelId) {
              return {
                subjectLevel: { id: subjectLevelId },
                gradeValueCalculateType: newMethod,
              };
            }
            const found = _.find(gradeValueMethod, {
              subjectLevel: { id: level.id },
            });
            return (
              found || {
                subjectLevel: { id: level.id },
                gradeValueCalculateType: MIDDLE,
              }
            );
          }),
        ];
      } else {
        newGradeValueMethod = [
          {
            subjectLevel: { id: null },
            gradeValueCalculateType: newMethod,
          },
          ..._.map(levels, level => ({
            subjectLevel: { id: level.id },
            gradeValueCalculateType: newMethod,
          })),
        ];
      }
    } else {
      newGradeValueMethod = newMethod;
    }
    const recalculated = recalculateGradeScaleValues({
      data: gradeScaleValuesData,
      gradeValueMethod: newGradeValueMethod,
      differentGradebookValuesForLevels,
    });
    setGradeValueMethod(newGradeValueMethod);
    setGradeScaleValuesData(recalculated);
  };

  const addRow = () => {
    const newRow = getNewRow({
      gradeScaleType,
      differentGradebookValuesForLevels,
      levels,
      shouldHideDefaultLevel,
    });
    setGradeScaleValuesData(
      updateDisplaySequenceForGradeScaleValues([
        ...gradeScaleValuesData,
        newRow,
      ])
    );
  };

  const addAfterCurrentRow = useCallback(
    args => {
      const { row: currentRowIndex } = args;
      const newRow = getNewRow({
        gradeScaleType,
        differentGradebookValuesForLevels,
        levels,
        shouldHideDefaultLevel,
      });

      const updatedData = [...gradeScaleValuesData];
      updatedData.splice(currentRowIndex + 1, 0, newRow);

      const recalculated = recalculateGradeScaleValues({
        data: updatedData,
        gradeValueMethod,
        differentGradebookValuesForLevels,
      });

      setGradeScaleValuesData(
        updateDisplaySequenceForGradeScaleValues(recalculated)
      );
    },
    [
      gradeScaleValuesData,
      gradeScaleType,
      gradeValueMethod,
      differentGradebookValuesForLevels,
      levels,
      setGradeScaleValuesData,
      shouldHideDefaultLevel,
    ]
  );

  const deleteRow = useCallback(
    args => {
      const { rowId } = args;
      const updatedData = _.filter(
        gradeScaleValuesData,
        row => row.id !== rowId
      );

      const recalculated = recalculateGradeScaleValues({
        data: updatedData,
        gradeValueMethod,
        differentGradebookValuesForLevels,
      });

      setGradeScaleValuesData(
        updateDisplaySequenceForGradeScaleValues(recalculated)
      );
    },
    [
      gradeScaleValuesData,
      gradeValueMethod,
      differentGradebookValuesForLevels,
      setGradeScaleValuesData,
    ]
  );

  const moveRow = useCallback(
    args => {
      const { row, iconName } = args;
      const newGradeScaleValuesData = _.cloneDeep(gradeScaleValuesData);

      if (iconName === "upArrowOutlined" && row > 0) {
        [newGradeScaleValuesData[row - 1], newGradeScaleValuesData[row]] = [
          newGradeScaleValuesData[row],
          newGradeScaleValuesData[row - 1],
        ];
      } else if (
        iconName === "downArrowOutlined" &&
        row < newGradeScaleValuesData.length - 1
      ) {
        [newGradeScaleValuesData[row], newGradeScaleValuesData[row + 1]] = [
          newGradeScaleValuesData[row + 1],
          newGradeScaleValuesData[row],
        ];
      } else {
        return;
      }

      const recalculated = recalculateGradeScaleValues({
        data: newGradeScaleValuesData,
        gradeValueMethod,
        differentGradebookValuesForLevels,
      });

      setGradeScaleValuesData(
        updateDisplaySequenceForGradeScaleValues(recalculated)
      );
    },
    [
      gradeScaleValuesData,
      gradeValueMethod,
      differentGradebookValuesForLevels,
      setGradeScaleValuesData,
    ]
  );

  const handleCellEdit = params => {
    const updatedGradeScaleValuesData = _.reduce(
      params,
      (accumulatedData, param) => {
        if (!param) return accumulatedData;

        const { cellCoods, newValue } = param;
        const { rowId, colId } = cellCoods;
        const { value, checked, cellType } = newValue;
        const columnKey = _.get(
          _.find(_.flatten(headers), { id: colId }),
          "columnKey",
          null
        );

        const newGradeScaleValuesData = _.cloneDeep(accumulatedData);
        const rowIndex = _.findIndex(newGradeScaleValuesData, { id: rowId });

        if (rowIndex === -1) return accumulatedData;

        switch (columnKey) {
          case "abbreviation":
            newGradeScaleValuesData[
              rowIndex
            ].abbreviation = isAbbreviationLengthLimited
              ? _.join(_.slice(value, 0, 4), "")
              : value;
            break;
          case "label":
            newGradeScaleValuesData[rowIndex].label = value;
            break;
          case "description":
            newGradeScaleValuesData[rowIndex].description = value;
            break;
          case "color": {
            newGradeScaleValuesData[rowIndex].color = value;
            break;
          }
          case "MasteryLevel": {
            if (cellType === "radio" && checked) {
              const previousMasteryIndex = _.findIndex(
                newGradeScaleValuesData,
                row =>
                  _.get(row, "gradeScaleValueScores[0].isMasteryLevel") === true
              );

              if (previousMasteryIndex !== -1) {
                const previousRowScores = _.get(
                  newGradeScaleValuesData,
                  [previousMasteryIndex, "gradeScaleValueScores"],
                  []
                );
                _.forEach(previousRowScores, (score, scoreIndex) => {
                  _.set(
                    newGradeScaleValuesData,
                    [
                      previousMasteryIndex,
                      "gradeScaleValueScores",
                      scoreIndex,
                      "isMasteryLevel",
                    ],
                    false
                  );
                });
                for (let i = rowIndex + 1; i <= previousMasteryIndex; i++) {
                  newGradeScaleValuesData[i].color =
                    colorPalette[_.random(0, _.size(colorPalette) - 1)];
                }
              }

              // Set isMasteryLevel to true for all levels in the current row
              const currentRowScores = _.get(
                newGradeScaleValuesData,
                [rowIndex, "gradeScaleValueScores"],
                []
              );
              _.forEach(currentRowScores, (score, scoreIndex) => {
                _.set(
                  newGradeScaleValuesData,
                  [
                    rowIndex,
                    "gradeScaleValueScores",
                    scoreIndex,
                    "isMasteryLevel",
                  ],
                  true
                );
              });
              for (let i = 0; i <= rowIndex; i++) {
                newGradeScaleValuesData[i].color = "var(--teal-600)";
              }
            }
            break;
          }
          case "Cutoff": {
            const numberValue = _.toNumber(value) || 0;
            const currentMinValue = numberValue > 100 ? 100 : numberValue;

            // calculate index for gradeScaleValueScores for levels
            let idx = differentGradebookValuesForLevels
              ? _.findIndex(
                  _.get(
                    newGradeScaleValuesData,
                    `[${rowIndex}].gradeScaleValueScores`
                  ),
                  item =>
                    _.get(item, "subjectLevel.id", null) ===
                    _.first(_.split(colId, "_"))
                )
              : 0;
            if (idx === -1) idx = 0;

            _.set(
              newGradeScaleValuesData,
              [rowIndex, "gradeScaleValueScores", idx, "minValue"],
              currentMinValue
            );

            const recalculated = recalculateGradeScaleValues({
              data: newGradeScaleValuesData,
              gradeValueMethod,
              differentGradebookValuesForLevels,
            });
            return recalculated;
          }
          case "GradeValue": {
            if (
              newGradeScaleValuesData[rowIndex].gradeScaleValueScores?.length >
              0
            ) {
              // calculate index for gradeScaleValueScores for levels
              let idx = differentGradebookValuesForLevels
                ? _.findIndex(
                    newGradeScaleValuesData[rowIndex].gradeScaleValueScores,
                    s =>
                      _.get(s, "subjectLevel.id", null) ===
                      _.first(_.split(colId, "_"))
                  )
                : 0;
              if (idx === -1) idx = 0;

              // calculate gradeValueMethod for the particular level
              let methodForLevel = gradeValueMethod;
              if (differentGradebookValuesForLevels) {
                const subjectLevelId = _.get(
                  newGradeScaleValuesData,
                  [
                    rowIndex,
                    "gradeScaleValueScores",
                    idx,
                    "subjectLevel",
                    "id",
                  ],
                  null
                );

                methodForLevel = _.get(
                  _.find(
                    gradeValueMethod,
                    item =>
                      _.get(item, "subjectLevel.id", null) === subjectLevelId
                  ),
                  "gradeValueCalculateType",
                  MIDDLE
                );
              }

              if (methodForLevel === CUSTOM) {
                const valueAsNumber = parseFloat(value) || 0;

                _.set(
                  newGradeScaleValuesData,
                  [rowIndex, "gradeScaleValueScores", idx, "gradeValue"],
                  valueAsNumber > 100 ? 100 : valueAsNumber
                );
              } else {
                // Use recalculateGradeScaleValues for all rows
                const recalculated = recalculateGradeScaleValues({
                  data: newGradeScaleValuesData,
                  gradeValueMethod,
                  differentGradebookValuesForLevels,
                });
                return recalculated;
              }
            }
            break;
          }
          case "dependentGradeScale": {
            const path = [rowIndex, "dependentGradeScaleValues"];
            const existingValues = _.get(newGradeScaleValuesData, path, []);

            const selectedValues = _.map(value, item => ({
              id: _.get(item, "id"),
              abbreviation: _.get(item, "label"),
              gradeScale: { id: colId },
            }));

            // Remove all existing values for this grade scale
            const filteredValues = _.filter(
              existingValues,
              item => _.get(item, "gradeScale.id") !== colId
            );

            // Add all new selected values
            const updatedValues = [...filteredValues, ...selectedValues];

            _.set(newGradeScaleValuesData, path, updatedValues);
            break;
          }
          case "GradePoint": {
            const valueAsNumber = parseFloat(value) || 0;
            _.set(
              newGradeScaleValuesData,
              [rowIndex, "gradeScaleValueGradePoints", 0, "gradePoint"],
              valueAsNumber
            );
            break;
          }
          case "AddedValue": {
            _.set(
              newGradeScaleValuesData,
              [
                rowIndex,
                "gradeScaleValueGradePoints",
                0,
                "isAddedValueApplicable",
              ],
              checked
            );
            break;
          }
        }

        return newGradeScaleValuesData;
      },
      _.cloneDeep(gradeScaleValuesData)
    );

    setGradeScaleValuesData(updatedGradeScaleValuesData);
  };

  const headers = getDatagridHeaders({
    t,
    gradeScaleValuesData,
    gradeScaleType,
    gradeType,
    curriculumType,
    showMasteryLevel,
    showCreditGpaCalculation,
    selectedDependentGradeScales,
    selectedDependentGradeScalesDetails,
    showActions,
    gradeValueMethod,
    differentGradebookValuesForLevels,
    levels,
    handleGradeValueChange: differentGradebookValuesForLevels
      ? (value, subjectLevelId) => handleGradeValueChange(value, subjectLevelId)
      : handleGradeValueChange,
    isEditMode,
    columnWidthMap,
    isFromCourseConfig,
    calculateStandardGradesUsingNumeric,
    containerWidth,
    isAbbreviationLengthLimited,
  });

  const data = useMemo(() => {
    return getGridData({
      t,
      gradeScaleValuesData,
      gradeScaleType,
      gradeType,
      gradeValueMethod,
      showMasteryLevel,
      showCreditGpaCalculation,
      isEditMode,
      showActions,
      selectedDependentGradeScales,
      isFromCourseConfig,
      allGradeScalesData,
      specialGradesTemplateId,
      calculateStandardGradesUsingNumeric,
      differentGradebookValuesForLevels,
      levels,
      moveRow,
      addAfterCurrentRow,
      deleteRow,
      showErrors,
      validationFields,
      isGradeScaleBeingUsed,
      shouldHideDefaultLevel,
    });
  }, [
    t,
    gradeScaleValuesData,
    gradeScaleType,
    gradeType,
    gradeValueMethod,
    showMasteryLevel,
    showCreditGpaCalculation,
    selectedDependentGradeScales,
    isEditMode,
    showActions,
    isFromCourseConfig,
    allGradeScalesData,
    specialGradesTemplateId,
    calculateStandardGradesUsingNumeric,
    differentGradebookValuesForLevels,
    levels,
    moveRow,
    addAfterCurrentRow,
    deleteRow,
    showErrors,
    validationFields,
    isGradeScaleBeingUsed,
    shouldHideDefaultLevel,
  ]);

  const datagridHeight = useMemo(() => {
    // header height (108px(mutli-level) or 64px(single level)) + row height (40px)
    const headerHeight = _.size(headers) < 2 ? 64 : 108;
    const rowHeight = 40;
    return `${headerHeight + data.length * rowHeight}px`;
  }, [headers, data]);

  return (
    <div className={classes.container} style={containerStyle}>
      <div
        ref={containerRef}
        className={classes.datagridContainer}
        style={{
          height: datagridHeight,
        }}
      >
        <GradebookDataGrid
          key={`${gradeValueMethod}-${data.length}`}
          headers={headers}
          data={data}
          isViewMode={!isEditMode}
          dataGridHeight="100%"
          dataGridWidth="100%"
          emptyCellStyle={styles.emptyCellStyle}
          onCellEdit={handleCellEdit}
          freezeColumns={0}
          tooltipDelayTime={300}
        />
      </div>

      {!isFromCourseConfig && isEditMode && showActions && (
        <div className={classes.actionContainer}>
          <Button
            dsVersion="2.0"
            icon={<AddOutlined />}
            variant="neutral"
            type="outlined"
            onClick={addRow}
          >
            {t("academicSetup:add_grade")}
          </Button>
        </div>
      )}
    </div>
  );
};

GradeScaleDatagridLegacy.defaultProps = {
  gradeType: ALPHA,
  gradeValueMethod: MIDDLE,
  setGradeValueMethod: () => {},
  isMasteryLevelEnabled: false,
  isUseForCreditGPACalculation: false,
  selectedDependentGradeScales: [],
  isEditMode: true,
  showActions: true,
  differentGradebookValuesForLevels: false,
  levels: [],
  gradeScaleValuesData: [],
  showErrors: false,
  calculateStandardGradesUsingNumeric: false,
  setGradeScaleValuesData: () => {},
  allGradeScalesData: [],
  specialGradesTemplateId: "",
  specialGradeScale: {},
  isUpdatingExistingTemplate: false,
};

const mapStateToProps = state => {
  const curriculumType = getCurrentCurriculumProgramType(state);

  return {
    curriculumType,
  };
};

const getDerivedProps = ownProps => {
  const {
    differentGradebookValuesForLevels,
    levels,
    gradeScaleValuesData,
  } = ownProps;

  // check differentGradebookValuesForLevels with feature flag
  const _differentGradebookValuesForLevels =
    shouldShowLevelsInGradingSetupV2() && differentGradebookValuesForLevels;

  // get levels data either from props or from gradeScaleValuesData
  const _levels =
    _.isNil(levels) && !_.isEmpty(gradeScaleValuesData)
      ? _.chain(_.get(gradeScaleValuesData, "[0].gradeScaleValueScores", []))
          .map("subjectLevel")
          .filter(Boolean)
          .uniqBy("id")
          .map(level => _.pick(level, ["id", "label", "abbreviation"]))
          .filter(level => level.id !== undefined)
          .value()
      : levels;

  return {
    differentGradebookValuesForLevels: _differentGradebookValuesForLevels,
    levels: _levels,
  };
};

export default compose(
  connect(mapStateToProps, null),
  withDerivedProps(getDerivedProps)
)(GradeScaleDatagridLegacy);
